'use client';

import { type ReactNode } from 'react';
import Link from 'next/link';

interface AuthShellProps {
  /** Pre-heading caps text — "Anmelden" / "Passwort zurücksetzen" / etc. */
  eyebrow?: string;
  /** Main heading — short, no marketing copy. */
  heading: string;
  /** One-line subtitle below the heading. */
  subtitle?: string;
  /** Form / interactive content. */
  children: ReactNode;
  /** Bottom slot — small links like "Passwort vergessen?" / "Noch kein Account?". */
  footer?: ReactNode;
}

/**
 * Shared layout for `/admin/login`, `/admin/forgot-password`,
 * `/admin/reset-password`, and any future auth screens.
 *
 * Stripe / Linear 2026 pattern:
 *   - Centered single column, max-w-sm (no marketing two-column for login —
 *     too noisy for an action page; register has the marketing pitch).
 *   - Background: light mesh-glow + dot-grid (matches the register page so
 *     auth flows feel like one product, not glued-together screens).
 *   - Logo top-left, "Zurück" link top-right (instead of a centred logo
 *     with a separate back arrow — less vertical real estate eaten).
 *   - No card/shadow/backdrop-blur. Form sits directly on the page so it
 *     feels lighter and renders faster.
 */
export function AuthShell({ eyebrow, heading, subtitle, children, footer }: AuthShellProps) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-white">
      {/* Background — same mesh-glow + dot-grid mask as /register so the
          auth flow feels like one design system, not five unrelated forms. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/3 top-0 h-[600px] w-[900px] -translate-y-1/3 rounded-full bg-blue-100/35 blur-3xl" />
        <div className="absolute -right-40 top-1/2 h-[420px] w-[600px] rounded-full bg-indigo-100/25 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(15,23,42,0.08) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage:
              'radial-gradient(ellipse 80% 60% at 50% 20%, black 30%, transparent 80%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 80% 60% at 50% 20%, black 30%, transparent 80%)',
          }}
        />
      </div>

      {/* Top brand row */}
      <header className="relative">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-6 sm:px-6 lg:px-8">
          <Link
            href="/"
            className="flex items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            aria-label="Zur Startseite"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo.svg" alt="EazyBooking" className="h-8 w-auto" />
          </Link>
          <Link
            href="/"
            className="rounded-full border border-gray-200 bg-white/80 px-4 py-2 text-sm font-medium text-gray-700 backdrop-blur-sm transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
          >
            ← Zur Startseite
          </Link>
        </div>
      </header>

      {/* Form column — single, centred, max-w-sm so inputs feel tight and
          the eye lands on the CTA quickly. */}
      <main className="relative mx-auto flex max-w-sm flex-col px-4 pb-16 pt-8 sm:px-0 sm:pt-12">
        <div className="space-y-1.5">
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-600">
              {eyebrow}
            </p>
          )}
          <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.02em] text-gray-950 sm:text-[32px]">
            {heading}
          </h1>
          {subtitle && (
            <p className="text-sm leading-relaxed text-gray-600">{subtitle}</p>
          )}
        </div>

        <div className="mt-7">{children}</div>

        {footer && (
          <div className="mt-8 text-sm text-gray-600">{footer}</div>
        )}
      </main>
    </div>
  );
}

/**
 * Slim notice banner used by all auth pages for success / error states.
 * Dot + text, monochrome. No icons — keeps the page calm.
 */
export function AuthNotice({
  tone,
  children,
}: {
  tone: 'success' | 'error' | 'info';
  children: ReactNode;
}) {
  const classes = {
    success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500' },
    error: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', dot: 'bg-rose-500' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', dot: 'bg-blue-500' },
  }[tone];

  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${classes.bg} ${classes.border} ${classes.text}`}
    >
      <span
        className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${classes.dot}`}
        aria-hidden="true"
      />
      <p className="leading-relaxed">{children}</p>
    </div>
  );
}
